import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { LangProvider } from "./lib/i18n";
import { AuthProvider, useAuth, homePath } from "./lib/auth";
import AppShell from "./components/AppShell";
import Login from "./pages/Login";
import ChangePassword from "./pages/ChangePassword";
import SupervisorHome from "./pages/SupervisorHome";
import Scan from "./pages/Scan";
import LabelLink from "./pages/LabelLink";
import Activate from "./pages/Activate";
import BinDetail from "./pages/BinDetail";
import Bins from "./pages/Bins";
import Dashboard from "./pages/Dashboard";
import Attendance from "./pages/Attendance";
import Labels from "./pages/Labels";
import Releases from "./pages/Releases";
import Users from "./pages/admin/Users";
import Master from "./pages/admin/Master";
import Settings from "./pages/admin/Settings";
import LoginActivity from "./pages/admin/LoginActivity";
import Reports from "./pages/Reports";
import LabResults from "./pages/LabResults";
import Dispatch from "./pages/Dispatch";
import Incoming from "./pages/Incoming";

const Guard = ({ roles, children }) => {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return null;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homePath(user)} replace />;
  return children;
};

const Root = () => {
  const { user, ready } = useAuth();
  if (!ready) return null;
  return <Navigate to={user ? homePath(user) : "/login"} replace />;
};

const MGR = ["admin", "exporter", "farm_manager"];
const STAFF = ["admin", "exporter"];
const VIEW = [...MGR, "packing_house"];

function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Root />} />
            <Route path="/login" element={<Login />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/l/:token" element={<Guard><LabelLink /></Guard>} />
            <Route element={<Guard><AppShell /></Guard>}>
              <Route path="/home" element={<SupervisorHome />} />
              <Route path="/scan" element={<Scan />} />
              <Route path="/activate/:number" element={<Guard roles={["admin", "farm_manager", "supervisor"]}><Activate /></Guard>} />
              <Route path="/bin/:id" element={<BinDetail />} />
              <Route path="/bins" element={<Bins />} />
              <Route path="/dashboard" element={<Guard roles={VIEW}><Dashboard /></Guard>} />
              <Route path="/incoming" element={<Guard roles={[...STAFF, "packing_house"]}><Incoming /></Guard>} />
              <Route path="/dispatch" element={<Guard roles={MGR}><Dispatch /></Guard>} />
              <Route path="/lab" element={<Guard roles={MGR}><LabResults /></Guard>} />
              <Route path="/reports" element={<Guard roles={VIEW}><Reports /></Guard>} />
              <Route path="/attendance" element={<Guard roles={MGR}><Attendance /></Guard>} />
              <Route path="/labels" element={<Guard roles={MGR}><Labels /></Guard>} />
              <Route path="/releases" element={<Guard roles={STAFF}><Releases /></Guard>} />
              <Route path="/admin/users" element={<Guard roles={["admin"]}><Users /></Guard>} />
              <Route path="/admin/logins" element={<Guard roles={["admin"]}><LoginActivity /></Guard>} />
              <Route path="/admin/master" element={<Guard roles={["admin"]}><Master /></Guard>} />
              <Route path="/admin/settings" element={<Guard roles={["admin"]}><Settings /></Guard>} />
            </Route>
            <Route path="*" element={<Root />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-center" richColors visibleToasts={2} duration={2500} />
      </AuthProvider>
    </LangProvider>
  );
}

export default App;
